import {useEffect, useMemo, useState} from 'react'
import {Navigate, useNavigate, useParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Skeleton} from '@/components/ui/skeleton'
import {Checkbox} from '@/components/ui/checkbox'
import {ConfirmDialog} from '@/components/confirm-dialog'
import {FixedStrip} from '@/components/fixed-strip'
import {KairosMark} from '@/components/kairos-mark'
import {SectionCard} from '@/components/section-card'
import {Hint} from '@/components/ui/hint'
import {PorQue} from '@/components/ui/por-que'
import {CuestionarioBlock, FiltroRow} from '@/components/bateria/digitacion-rejilla'
import {FichaDatosGenerales} from '@/components/bateria/ficha-datos-generales'
import {HelpIcon} from '@/components/help-icon'
import {useIsSystemUser} from '@/hooks/empresa/organizacion/use-businesses'
import {useBorradorDigitacion} from '@/hooks/bateria/use-borrador-digitacion'
import {useEvaluacionDetail, useFormaEstructura, useDigitacionManual} from '@/hooks/bateria/use-bateria'
import {motivoDelError, translateApiError} from '@/lib/errors'
import {formatDateTime} from '@/lib/risk'

export function AdminBateriaDigitacionPage() {
  const {t} = useTranslation()
  const navigate = useNavigate()
  const {businessId, evaluacionId, paqueteId} = useParams<{
    businessId: string
    evaluacionId: string
    paqueteId: string
  }>()
  const isSystemUser = useIsSystemUser()
  const {data: detail} = useEvaluacionDetail(businessId, evaluacionId, isSystemUser)
  const paquete = detail?.paquetes.find((p) => p.id === paqueteId)
  const {data: estructura} = useFormaEstructura(paquete?.forma, isSystemUser && !!paquete)
  const save = useDigitacionManual(businessId ?? '', evaluacionId ?? '', paqueteId ?? '')

  // Dos estados: con uno solo, marcar la casilla desmontaba el intersticial antes de pulsar «Continuar».
  const [declarado, setDeclarado] = useState(false)
  const [entro, setEntro] = useState(false)
  const [respuestas, setRespuestas] = useState<Record<string, string | null>>({})
  const [error, setError] = useState<string | null>(null)
  const [saliendo, setSaliendo] = useState(false)
  const {guardado, escribir, descartar} = useBorradorDigitacion(paqueteId ?? '')
  const [borradorResuelto, setBorradorResuelto] = useState(guardado === null)

  const backTo = `/admin/bateria/empresas/${businessId}/evaluaciones/${evaluacionId}`

  const answeredCount = useMemo(
    () => Object.values(respuestas).filter((v) => v != null && v !== '').length,
    [respuestas]
  )
  const totalCount = useMemo(() => {
    if (!estructura) return 0
    const cuestionarioItems = estructura.cuestionarios.reduce(
      (sum, c) => sum + c.grupos.reduce((gSum, g) => gSum + g.items.length, 0),
      0
    )
    return estructura.filtros.length + cuestionarioItems
  }, [estructura])

  useEffect(() => {
    escribir(respuestas)
  }, [respuestas, escribir])

  if (!isSystemUser || !businessId || !evaluacionId || !paqueteId) return <Navigate to="/" replace />

  function setItem(itemId: string, value: string) {
    setRespuestas((prev) => ({...prev, [itemId]: value}))
  }

  function salir() {
    descartar()
    navigate(backTo)
  }

  function intentarSalir() {
    if (answeredCount > 0) {
      setSaliendo(true)
      return
    }
    salir()
  }

  function submit() {
    setError(null)
    save.mutate(
      {respuestas, campos_ficha: {}},
      {
        onSuccess: () => {
          descartar()
          navigate(backTo)
        },
        onError: (err) => {
          const detalle = motivoDelError(err)
          setError(
            detalle?.motivo === 'ficha_incompleta'
              ? t('admin.bateria.digitacion.fichaIncompleta')
              : translateApiError(err)
          )
        },
      }
    )
  }

  return (
    <div className="space-y-5" data-testid="bateria-digitacion">
      <div>
        <button
          type="button"
          data-testid="digitacion-volver"
          className="font-mono text-[11px] text-primary hover:underline"
          onClick={intentarSalir}>
          {t('admin.bateria.digitacion.back')}
        </button>
        <div className="mt-2 flex items-center gap-2 text-foreground">
          <KairosMark />
          <h1 className="font-heading text-xl">{t('admin.bateria.digitacion.title')}</h1>
        </div>
        {paquete && (
          <p className="mt-1 text-sm text-muted-foreground">
            {paquete.employee_name} · {t('admin.bateria.formaValue', {forma: paquete.forma})}
          </p>
        )}
      </div>

      {guardado !== null && !borradorResuelto && (
        <div
          className="notice-amber flex flex-wrap items-center justify-between gap-2 rounded-lg px-4 py-3 text-sm"
          data-testid="digitacion-borrador">
          <span>
            {t('admin.bateria.digitacion.borradorAviso', {
              count: Object.values(guardado.respuestas).filter((v) => v != null && v !== '').length,
              fecha: formatDateTime(new Date(guardado.en).toISOString()),
            })}
          </span>
          <span className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              data-testid="digitacion-borrador-continuar"
              onClick={() => {
                setRespuestas(guardado.respuestas)
                setBorradorResuelto(true)
              }}>
              {t('admin.bateria.digitacion.borradorContinuar')}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              data-testid="digitacion-borrador-descartar"
              onClick={() => {
                descartar()
                setBorradorResuelto(true)
              }}>
              {t('admin.bateria.digitacion.borradorDescartar')}
            </Button>
          </span>
        </div>
      )}

      {!entro ? (
        <SectionCard
          testId="digitacion-interstitial"
          className="mx-auto max-w-2xl"
          title={t('admin.bateria.digitacion.interstitialTitle')}
          bodyClassName="space-y-4 p-5">
          <div className="notice-amber flex flex-wrap items-center gap-2 rounded-lg px-4 py-3 text-sm">
            {t('admin.bateria.digitacion.warning')}
            <PorQue
              label={t('admin.bateria.digitacion.porqueLabel')}
              testId="digitacion-porque"
              contenidoTestId="digitacion-porque-contenido">
              {t('admin.bateria.digitacion.porque')}
            </PorQue>
          </div>
          <label className="flex items-start gap-2.5 text-sm text-foreground">
            <span className="pt-0.5">
              <Checkbox
                checked={declarado}
                onCheckedChange={(next) => setDeclarado(next)}
                data-testid="digitacion-atestacion"
                aria-label={t('admin.bateria.digitacion.attestLabel')}
              />
            </span>
            <span>{t('admin.bateria.digitacion.attestLabel')}</span>
          </label>
          <div className="surface-divider flex justify-between border-t pt-4">
            <Button variant="outline" onClick={intentarSalir}>
              {t('common.cancel')}
            </Button>
            <Button disabled={!declarado} data-testid="digitacion-continuar" onClick={() => setEntro(true)}>
              {t('admin.bateria.digitacion.continue')}
            </Button>
          </div>
        </SectionCard>
      ) : !estructura ? (
        <Skeleton className="h-96 w-full" />
      ) : (
        <div className="space-y-6" data-testid="digitacion-grid">
          <FixedStrip testId="digitacion-progreso-sticky">
            <div className="surface relative mx-auto flex max-w-5xl items-center justify-between gap-3 overflow-hidden px-4 py-2 shadow-[var(--shadow-pop)]">
              <span className="font-mono text-xs font-medium text-foreground">
                {t('admin.bateria.digitacion.progress', {answered: answeredCount, total: totalCount})}
              </span>
              <span className="font-mono text-xs text-muted-foreground">
                {t('admin.bateria.digitacion.remaining', {count: Math.max(totalCount - answeredCount, 0)})}
              </span>
              <span
                className="absolute inset-x-0 bottom-0 h-[3px] bg-track"
                role="progressbar"
                aria-valuenow={answeredCount}
                aria-valuemin={0}
                aria-valuemax={totalCount}
                aria-label={t('admin.bateria.digitacion.progress', {answered: answeredCount, total: totalCount})}
                data-testid="digitacion-progreso-riel">
                <span
                  className="block h-full bg-primary transition-all"
                  style={{width: `${totalCount > 0 ? Math.round((answeredCount / totalCount) * 100) : 0}%`}}
                />
              </span>
            </div>
          </FixedStrip>

          <div className="notice-amber flex flex-wrap items-center justify-between gap-2 rounded-lg px-4 py-2.5 text-sm">
            <span>{t('admin.bateria.digitacion.manualBadge')}</span>
          </div>

          {/* Va antes de la rejilla porque sin `tipo_cargo` el guardado se rechaza. */}
          <SectionCard testId="digitacion-ficha" title={t('admin.bateria.digitacion.fichaTitle')}>
            <Hint testId="digitacion-ficha-hint">{t('admin.bateria.digitacion.fichaHint')}</Hint>
            <FichaDatosGenerales businessId={businessId} evaluacionId={evaluacionId} paqueteId={paqueteId} enabled />
          </SectionCard>

          {/* Los filtros condicionales primero: su Sí/No gobierna qué bloques aplican. */}
          <SectionCard
            testId="digitacion-filtros"
            title={
              <span className="inline-flex items-center gap-1.5">
                {t('admin.bateria.digitacion.filtrosTitle')}
                <HelpIcon
                  label={t('common.help.iconLabelFor', {campo: t('admin.bateria.digitacion.filtrosTitle')})}
                  testId="digitacion-filtros-tip">
                  {t('admin.bateria.digitacion.filtrosHint')}
                </HelpIcon>
              </span>
            }
            bodyClassName="px-5 py-2">
            {estructura.filtros.map((filtro) => (
              <FiltroRow
                key={filtro.item_id}
                itemId={filtro.item_id}
                condicional={filtro.condicional}
                value={respuestas[filtro.item_id] ?? null}
                onChange={(valor) => setItem(filtro.item_id, valor)}
              />
            ))}
          </SectionCard>

          {estructura.cuestionarios.map((cuestionario) => (
            <CuestionarioBlock
              key={cuestionario.cuestionario}
              cuestionario={cuestionario}
              respuestas={respuestas}
              onChange={setItem}
            />
          ))}

          {error && (
            <p className="text-sm text-destructive" data-testid="digitacion-error">
              {error}
            </p>
          )}
          <div className="surface flex flex-wrap items-center justify-between gap-3 px-5 py-3.5">
            <span className="font-mono text-xs text-muted-foreground">
              {t('admin.bateria.digitacion.progress', {answered: answeredCount, total: totalCount})}
            </span>
            <div className="flex gap-2">
              <Button variant="outline" data-testid="digitacion-cancelar" onClick={intentarSalir}>
                {t('common.cancel')}
              </Button>
              <Button data-testid="digitacion-guardar" disabled={save.isPending} onClick={submit}>
                {save.isPending ? t('common.saving') : t('admin.bateria.digitacion.save')}
              </Button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={saliendo}
        onOpenChange={setSaliendo}
        title={t('admin.bateria.digitacion.salida.titulo', {count: answeredCount})}
        description={t('admin.bateria.digitacion.salida.cuerpo')}
        porque={{
          label: t('admin.bateria.digitacion.salida.porqueLabel'),
          testId: 'digitacion-salida-porque',
          contenidoTestId: 'digitacion-salida-porque-contenido',
          contenido: <p>{t('admin.bateria.digitacion.salida.porqueBody')}</p>,
        }}
        confirmLabel={t('admin.bateria.digitacion.salida.salir')}
        cancelLabel={t('admin.bateria.digitacion.salida.quedarse')}
        onConfirm={() => {
          setSaliendo(false)
          salir()
        }}
      />
    </div>
  )
}
